// ============================================================
// preProcessReuse.js — reusable SVG blocks, expanded before cues load:
//   <g id="reuse(name)"> … </g>       ← definition
//   <g id="use(name)">               ← inject at original location
//   <g id="use(name)@self">          ← inject at placeholder location
// Works in scroll mode + page mode.
// ============================================================

import {
  stripAllTransforms,
  alignCloneAtPlaceholder_TopLeft,
  uniqueReuseId
} from "./utils.js";
import { parseUseDirective } from "./parser.js";
import { onReset } from "../system/teardown.js";

export let reuseRegistry = Object.create(null);

/** Resolve the base URL for /pages/ */
function getPagesBase(userProvided) {
  if (userProvided) return userProvided.replace(/\/+$/, "") + "/";
  if (typeof window !== "undefined" && window.pagesDir) {
    return ("" + window.pagesDir).replace(/\/+$/, "") + "/";
  }
  return "/pages/";
}

// ------------------------------------------------------------
// 1) Register reusable blocks in the currently loaded SVG
// ------------------------------------------------------------
export function registerReuseBlocks(svgRoot) {
  if (!svgRoot) return;

  window.reuseRegistry = window.reuseRegistry || {};
  const defs = svgRoot.querySelectorAll('g[id^="reuse("]');

  defs.forEach(g => {
    // Strip Inkscape-added suffix like -2, -7 before matching
    const cleanId = g.id.replace(/\)-\d+$/, ')');
    const m = cleanId.match(/^reuse\(\s*([^)]+)\s*\)$/);
    if (!m) return;

    const name = m[1].trim();
    const originalTransform = g.getAttribute("transform") || "";

    reuseRegistry[name] = {
      template: g.cloneNode(true),
      sourceTransform: originalTransform
    };

  });

  window.reuseRegistry = reuseRegistry;
}

// ------------------------------------------------------------
// 2) Preload reusable blocks from all SVGs in /pages/
// ------------------------------------------------------------
export async function preloadReuseBlocksFromPages(pagesUrl) {
  const base = getPagesBase(pagesUrl);

  try {
    const res = await fetch(base, { cache: "no-store" });
    if (!res.ok) {
      console.warn(`[reuse] ⚠ Cannot read directory index at ${base} (HTTP ${res.status}).`);
      return;
    }

    const html = await res.text();
    const svgFiles = [...html.matchAll(/href="([^"]+\.svg)"/gi)]
      .map(m => decodeURIComponent(m[1]).split("/").pop())
      .filter(fname => fname && !fname.startsWith(".") && !fname.startsWith("?"));

    if (!svgFiles.length) {
      console.warn(`[reuse] ⚠ no .svg files in ${base}`);
      return;
    }


    await Promise.all(svgFiles.map(async fname => {
      const url = base + fname;

      try {
        const r = await fetch(url, { cache: "no-store" });
        if (!r.ok) return;

        const text = await r.text();
        const doc = new DOMParser().parseFromString(text, "image/svg+xml");
        if (doc.querySelector("parsererror")) return;

        doc.querySelectorAll('g[id^="reuse("]').forEach(g => {
          // Strip Inkscape-added suffix like -2, -7 before matching
          const cleanId = g.id.replace(/\)-\d+$/, ')');
          const m = cleanId.match(/^reuse\(\s*([^)]+)\s*\)$/);
          if (!m) return;

          const name = m[1].trim();
          const originalTransform = g.getAttribute("transform") || "";

          reuseRegistry[name] = {
            template: g.cloneNode(true),
            sourceTransform: originalTransform
          };

          reuseRegistry[name].template.setAttribute("data-reuse-source", url);
        });

      } catch (err) {
        console.warn(`[reuse] ❌ error reading ${url}:`, err);
      }
    }));


  } catch (err) {
    console.warn(`[reuse] ❌ preloadReuseBlocksFromPages() failed:`, err);
  }

  window.reuseRegistry = reuseRegistry;
}

// ------------------------------------------------------------
// 3) Insert reusable blocks (with transform preservation)
// ------------------------------------------------------------
export function handleUse(raw, placeholder, scopeSvg = null) {
  const svg = scopeSvg || (placeholder && placeholder.ownerSVGElement);
  if (!svg || !placeholder) return null;

  // Parse name and placement
  const at = raw.indexOf('@');
  const name = (at >= 0 ? raw.slice(0, at) : raw).trim();
  const placement = (at >= 0 ? raw.slice(at + 1) : '').trim(); // "self" or ""

  // Lookup registry entry
  const reg = window.reuseRegistry || {};
  const def = reg[name];

  if (!def || !def.template) {
    console.warn(`[reuse] missing block "${name}"`);
    placeholder.remove();
    return null;
  }

  const parent = placeholder.parentNode;
  if (!parent) return null;

  // ------------------------------------------------------------
  // Clone template group (deep clone)
  // ------------------------------------------------------------
  let clone = def.template.cloneNode(true);

  // Ensure wrapper is <g>
  if (clone.tagName?.toLowerCase() !== "g") {
    const g = document.createElementNS("http://www.w3.org/2000/svg", "g");
    g.appendChild(clone);
    clone = g;
  }

// ------------------------------------------------------------
// Remove internal transforms from text (Inkscape artefacts)
// ------------------------------------------------------------
clone.querySelectorAll("text[transform]").forEach(el => {
  console.warn("[reuse] Removing internal text transform:", el.getAttribute("transform"));
  el.removeAttribute("transform");
});

  // ------------------------------------------------------------
  // Preserve all internal transforms
  // (critical for nested translations, button placeholders, etc.)
  // ------------------------------------------------------------
  const origTransEls = def.template.querySelectorAll("[transform]");
  const cloneTransEls = clone.querySelectorAll("[transform]");

  cloneTransEls.forEach((el, i) => {
    const orig = origTransEls[i];
    if (orig) {
      const t = orig.getAttribute("transform");
      if (t) el.setAttribute("transform", t);
    }
  });

  // ------------------------------------------------------------
  // Unique reusable id
  // ------------------------------------------------------------
  const baseId = (def.template.id || `reuse(${name})`).replace(/__[a-z0-9]+$/i, "");
  clone.id = uniqueReuseId(baseId);

  // ------------------------------------------------------------
  // Insert clone before placeholder
  // ------------------------------------------------------------
  parent.insertBefore(clone, placeholder);

  // ------------------------------------------------------------
  // Placement rules
  // ------------------------------------------------------------
  if (placement === "self") {
    // Completely flatten & align clone using placeholder geometry
    stripAllTransforms(clone);
    alignCloneAtPlaceholder_TopLeft(clone, placeholder);

  } else {
    // Default: flatten, then apply template transform once
    stripAllTransforms(clone);

    const t =
      def.sourceTransform ||
      def.template.getAttribute("transform") ||
      "";

    if (t) clone.setAttribute("transform", t);
  }

  // ------------------------------------------------------------
  // Remove the <g id="use(...)"> placeholder safely
  // ------------------------------------------------------------
  placeholder.remove();

  return clone;
}


// ------------------------------------------------------------
// 4) Auto-scan SVG and inject all use() references
// ------------------------------------------------------------
export function autoInjectUseBlocks(svgRoot) {
  if (!svgRoot) return;

  window.reuseRegistry = window.reuseRegistry || {};
  const placeholders = Array.from(svgRoot.querySelectorAll('[id^="use("]'));

  placeholders.forEach(ph => {
    const id = ph.id || "";

    // The folder form first: its parameters can contain nested parens
    // (index:Prand(0,1,2)), which the name regex below cannot match.
    const folderSpec = parseUsePlaceholder(id);
    if (folderSpec) { handleUseFolder(folderSpec, ph); return; }

    const m = id.match(/^use\(\s*([^)]+)\s*\)(?:@(\w+))?$/);
    if (!m) return;

    const name = m[1].trim();
    const placement = m[2]?.trim(); // "self" or undefined

    handleUse(
      placement ? `${name}@${placement}` : name,
      ph,
      svgRoot
    );
  });
}


// ============================================================
//  FOLDERS OF FRAGMENTS — use(path:…)
// ============================================================
//
//   use(path:blocks)                       the first file
//   use(path:blocks, index:2)              the third file (0-based)
//   use(path:blocks, index:rand(0,7))      a different one every load
//   use(path:blocks, index:Prand(0,1,2))   stepped across placeholders
//
// The files are whole SVGs under the project's blocks/ folder, and — unlike
// image(path:…), which renders a picture through <image href> and therefore
// cannot see inside it — they are INLINED into the score. Their cues are
// ordinary cues from that moment on: use() is expanded by autoInjectUseBlocks,
// which runs before animationAssign and assignCues (SVGInit.js), so anything
// the fragment carries is picked up on the same pass as the rest of the score.
//
// Each fragment is inserted as a nested <svg> sized to the placeholder. That
// is what keeps a fragment's own coordinate system its own: the file is
// authored at whatever size suits it, and its viewBox scales it into the slot
// without any transform arithmetic here.

/** folder name → ordered array of { file, root } */
export let useFolderRegistry = Object.create(null);

/** Per-pattern cursors, so successive placeholders step a Pseq rather than all showing item 0. */
const _patternCursors = new Map();

/** Read a use() placeholder id, or null if it is not the folder form. */
export function parseUsePlaceholder(id) {
  const at = String(id || "").indexOf("@");
  const core = at >= 0 ? id.slice(0, at) : id;
  const placement = at >= 0 ? id.slice(at + 1).trim() : "";
  const ast = parseUseDirective(core);
  if (!ast) return null;
  const p = {};
  for (const a of (ast.args || [])) if (a?.type) p[a.type] = a.value;
  if (p.path == null) return null;              // use(name) — the legacy form
  return { folder: String(p.path), index: p.index, placement };
}

/**
 * Which file a placeholder should show.
 *
 * Accepts a plain number, a rand()/irand() call, or a Pseq/Prand/Pshuf
 * pattern. Out-of-range indices wrap rather than fail: a folder gaining or
 * losing a file should not silently blank a score that indexes past the end.
 *
 * @param {*} spec        the index: value, already parsed
 * @param {number} count  how many files the folder holds
 * @param {string} key    identity for the pattern cursor (folder + pattern)
 * @returns {number} an index in 0..count-1
 */
export function resolveFolderIndex(spec, count, key = "") {
  if (!count || count < 1) return 0;
  const wrap = (n) => ((Math.round(Number(n) || 0) % count) + count) % count;

  if (spec == null) return 0;
  if (typeof spec === "number") return wrap(spec);

  if (spec.type === "funcCall") {
    const [a = 0, b = count - 1] = spec.args || [];
    const lo = Math.min(Number(a), Number(b)), hi = Math.max(Number(a), Number(b));
    return wrap(lo + Math.floor(Math.random() * (hi - lo + 1)));
  }

  if (spec.type === "pattern") {
    const values = (spec.values || []).filter((v) => typeof v === "number");
    if (!values.length) return 0;
    // A trailing bare number in any pattern is a REPEAT COUNT, so Prand(0,1,2)
    // means "0 and 1, twice" and quietly loses the 2. Folder indices are all
    // bare numbers, so this is easy to walk into; say so rather than silently
    // dropping a file from the selection.
    // ...but `inf` is the documented way to say "choose from all of them", and
    // it arrives here as repeats:Infinity — a number. Warning on it told people
    // to fix the one spelling that was already right, and built the suggestion
    // by splicing Infinity into the value list. Only a FINITE count means a
    // real index was swallowed.
    if (Number.isFinite(spec.repeats)) {
      console.warn(
        `[use] ${spec.name}(…, ${spec.repeats}) — a trailing number is a repeat count, ` +
        `not an index. Write ${spec.name}(${[...values, spec.repeats].join(",")},inf) ` +
        `to select from all of them.`
      );
    }
    const name = String(spec.name || "Pseq").toLowerCase();
    if (name === "prand" || name === "pxrand") {
      return wrap(values[Math.floor(Math.random() * values.length)]);
    }
    // Pseq and friends advance one step per placeholder that uses them.
    const cursor = _patternCursors.get(key) ?? 0;
    _patternCursors.set(key, cursor + 1);
    return wrap(values[cursor % values.length]);
  }

  return 0;
}

/** Forget the reuse() blocks: the next project registers its own. */
export function resetReuseBlocks() {
  reuseRegistry = Object.create(null);
  if (typeof window !== "undefined") window.reuseRegistry = reuseRegistry;
}

/** Forget the folders and their pattern cursors — a fresh score starts a Pseq at its first step. */
export function resetUseFolders() {
  _patternCursors.clear();
  useFolderRegistry = Object.create(null);
}
// a project's reuse() blocks, and its use(path:) folders (keyed by folder
// name, so another project's blocks/ would be served from them) with their
// Pseq cursors
onReset("project", resetReuseBlocks);
onReset("project", resetUseFolders);

/**
 * Fetch every folder that this score's use(path:…) placeholders reference.
 *
 * Runs in the async preload phase alongside preloadReuseBlocksFromPages, so
 * the injection pass that follows stays synchronous.
 */
export async function preloadUseFolders(svgRoot, projectName = window.currentProjectName) {
  if (!svgRoot || !projectName) return;

  const wanted = new Set();
  for (const ph of svgRoot.querySelectorAll('[id^="use("]')) {
    const spec = parseUsePlaceholder(ph.id);
    if (spec) wanted.add(spec.folder);
  }
  if (!wanted.size) return;

  await Promise.all([...wanted].map(async (folder) => {
    if (useFolderRegistry[folder]) return;                 // already loaded
    const listUrl = `/api/block-list/${encodeURIComponent(projectName)}/` +
                    folder.split("/").map(encodeURIComponent).join("/");
    let files = [];
    try {
      const r = await fetch(listUrl, { cache: "no-store" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      files = Array.isArray(d.files) ? d.files : [];
    } catch (err) {
      console.warn(`[use] could not list blocks/${folder}: ${err.message}`);
      return;
    }
    if (!files.length) {
      console.warn(`[use] no .svg files in blocks/${folder}`);
      return;
    }

    const loaded = [];
    for (const file of files) {                            // in listed order
      const url = `/scores/${encodeURIComponent(projectName)}/blocks/` +
                  folder.split("/").map(encodeURIComponent).join("/") +
                  `/${encodeURIComponent(file)}`;
      try {
        const r = await fetch(url, { cache: "no-store" });
        if (!r.ok) continue;
        const doc = new DOMParser().parseFromString(await r.text(), "image/svg+xml");
        if (doc.querySelector("parsererror") || !doc.documentElement) continue;
        loaded.push({ file, root: doc.documentElement });
      } catch { /* skip the unreadable one, keep the folder */ }
    }
    useFolderRegistry[folder] = loaded;
  }));

}

/**
 * Replace one use(path:…) placeholder with the fragment it selects.
 * @returns {Element|null} the inserted node
 */
export function handleUseFolder(spec, placeholder) {
  const entries = useFolderRegistry[spec.folder];
  if (!entries?.length) {
    console.warn(`[use] folder "${spec.folder}" is empty or was not preloaded`);
    return null;
  }
  const parent = placeholder.parentNode;
  if (!parent) return null;

  const key = `${spec.folder}|${JSON.stringify(spec.index ?? null)}`;
  const i = resolveFolderIndex(spec.index, entries.length, key);
  const chosen = entries[i];

  // A nested <svg> keeps the fragment's coordinates its own and scales it into
  // the placeholder's box via the fragment's viewBox.
  const box = placeholder.getBBox ? placeholder.getBBox() : null;
  const frag = document.importNode(chosen.root, true);
  if (box) {
    frag.setAttribute("x", box.x);
    frag.setAttribute("y", box.y);
    frag.setAttribute("width", box.width);
    frag.setAttribute("height", box.height);
  }
  frag.setAttribute("data-use-folder", spec.folder);
  frag.setAttribute("data-use-file", chosen.file);
  // NOT "use(...)": an injected fragment whose id starts with use( would be
  // picked up as a placeholder by the next injection pass, and is indexed by
  // every [id^="use("] selector in the codebase. The folder and file are on
  // data- attributes, which is where that information belongs.
  frag.id = uniqueReuseId(`usefrag-${spec.folder.replace(/[^\w-]/g, "-")}`);

  parent.insertBefore(frag, placeholder);
  try { placeholder.remove(); } catch { /* already gone */ }
  return frag;
}

// ------------------------------------------------------------
// Export globals
// ------------------------------------------------------------
if (typeof window !== "undefined") {
  window.registerReuseBlocks = registerReuseBlocks;
  window.preloadReuseBlocksFromPages = preloadReuseBlocksFromPages;
}

// ============================================================
// PAGE DISCOVERY
// ============================================================
// The server's JSON listing is authoritative (GET /api/project/:name/pages);
// scraping the directory index is kept as a fallback for a plain static host.
async function listPageFiles(base, projectName) {
  if (projectName) {
    try {
      const r = await fetch(`/api/project/${encodeURIComponent(projectName)}/pages`, { cache: "no-store" });
      const d = await r.json();
      if (d.ok && Array.isArray(d.pages)) return d.pages.map(n => `${n}.svg`);
    } catch { /* fall through to the directory index */ }
  }
  const res = await fetch(base, { cache: "no-store" });
  if (!res.ok) {
    console.warn(`[allPages] ⚠ Cannot read directory index at ${base}`);
    return [];
  }
  const html = await res.text();
  return [...html.matchAll(/href="([^"]+\.svg)"/gi)]
    .map(m => decodeURIComponent(m[1]).split("/").pop());
}

export async function buildPageRegistryFromDirIndex(projectName = window.currentProjectName) {
  const base = getPagesBase();

  try {
    const files = await listPageFiles(base, projectName);
    if (!files.length) {
      window.pageRegistry = {};
      return;
    }

    const registry = {};
    for (const fname of files) {
      registry[fname.replace(/\.svg$/i, "")] = base + fname;
    }

    window.pageRegistry = registry;

  } catch (err) {
    console.warn("[allPages] ❌ Failed to build pageRegistry:", err);
  }
}

if (typeof window !== "undefined") {
  window.buildPageRegistryFromDirIndex = buildPageRegistryFromDirIndex;
}


